"use client";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { FC } from "react";

import { LogOut } from "@/app/(main)/accounts/actions";
import { useAuth } from "@/contexts/auth_context";
import { useModal } from "@/contexts/modal_context";
import { usePublicSettings } from "@/contexts/public_settings_context";
import cn from "@/utils/core/cn";
import { formatUsername } from "@/utils/formatters/users";

type Props = {
  btnClassName?: string;
};

const NavUserButton: FC<Props> = ({ btnClassName }) => {
  const { setCurrentModal } = useModal();
  const { PUBLIC_ALLOW_SIGNUP } = usePublicSettings();
  const { user } = useAuth();
  const t = useTranslations();
  const { PUBLIC_ALLOW_TUTORIAL } = usePublicSettings();

  if (!user) {
    return (
      <div className="ml-auto mr-2 flex h-full items-center gap-2 p-0 sm:ml-9">
        <button
          className="hidden h-full w-full whitespace-nowrap rounded-md px-3 py-1 text-center capitalize text-gray-700 hover:bg-gray-300 min-[375px]:block"
          onClick={() => setCurrentModal({ type: "signin" })}
        >
          {t("logIn")}
        </button>
        {PUBLIC_ALLOW_SIGNUP && (
          <button
            className="h-7 w-full whitespace-nowrap rounded-full bg-blue-700 px-3 text-center capitalize text-white hover:bg-blue-800"
            onClick={() => setCurrentModal({ type: "signup" })}
          >
            {t("createAnAccount")}
          </button>
        )}
      </div>
    );
  }

  return (
    <Menu>
      <MenuButton
        className={cn(
          "ml-6 flex h-full items-center gap-1 p-3 text-gray-600 no-underline hover:bg-gray-100",
          btnClassName
        )}
      >
        {formatUsername(user)}
        <FontAwesomeIcon size="xs" icon={faChevronDown} />
      </MenuButton>
      <MenuItems
        anchor="bottom"
        className="z-100 border border-gray-300 bg-white text-sm text-gray-800 shadow-lg"
      >
        <MenuItem>
          <Link
            className="flex items-center justify-center whitespace-nowrap px-6 py-1.5 capitalize no-underline hover:bg-gray-100 lg:items-end lg:justify-end lg:text-right"
            href={`/accounts/profile/${user.id}`}
          >
            {t("profile")}
          </Link>
        </MenuItem>
        <MenuItem>
          <Link
            className="flex items-center justify-center whitespace-nowrap px-6 py-1.5 capitalize no-underline hover:bg-gray-100 lg:items-end lg:justify-end lg:text-right"
            href={"/accounts/settings/"}
          >
            {t("settings")}
          </Link>
        </MenuItem>
        {PUBLIC_ALLOW_TUTORIAL && (
          <MenuItem>
            <a
              className="flex cursor-pointer items-center justify-center whitespace-nowrap px-6 py-1.5 capitalize no-underline hover:bg-gray-100 lg:items-end lg:justify-end lg:text-right"
              onClick={() => setCurrentModal({ type: "onboarding" })}
            >
              {t("tutorial")}
            </a>
          </MenuItem>
        )}
        {user.is_superuser && (
          <>
            {!PUBLIC_ALLOW_SIGNUP && (
              <MenuItem>
                <Link
                  className="flex items-center justify-center whitespace-nowrap px-6 py-1.5 capitalize no-underline hover:bg-gray-100 lg:items-end lg:justify-end lg:text-right"
                  href={"/accounts/invite/"}
                >
                  {t("signupInviteUsers")}
                </Link>
              </MenuItem>
            )}
            <MenuItem>
              <Link
                className="flex items-center justify-center whitespace-nowrap px-6 py-1.5 capitalize no-underline hover:bg-gray-100 lg:items-end lg:justify-end lg:text-right"
                href={"/admin/"}
              >
                {t("admin")}
              </Link>
            </MenuItem>
          </>
        )}
        <MenuItem>
          <a
            className="flex cursor-pointer items-center justify-center whitespace-nowrap px-6 py-1.5 capitalize no-underline hover:bg-gray-100 lg:items-end lg:justify-end lg:text-right"
            onClick={() => {
              void LogOut();
            }}
          >
            {t("logOut")}
          </a>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
};

export default NavUserButton;
